import React, { useState } from 'react';
import type { EducationalQualification } from '../../services/unified/types';

interface EducationalQualificationsSectionProps {
  qualifications: EducationalQualification[];
  onChange: (qualifications: EducationalQualification[]) => void;
  error?: string;
}

const DEGREE_LEVELS = ['Graduate', 'Post-Graduate', 'Doctorate'] as const;

export const EducationalQualificationsSection: React.FC<EducationalQualificationsSectionProps> = ({
  qualifications,
  onChange,
  error,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formData, setFormData] = useState<Omit<EducationalQualification, 'id'>>({
    degree_level: 'Graduate',
    preferred_iitian: false,
    preferred_institutions: '',
    additional_notes: '',
  });

  // ============================================================================
  // Form Handlers
  // ============================================================================

  const handleInputChange = (
    field: keyof Omit<EducationalQualification, 'id'>,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const resetForm = () => {
    setFormData({
      degree_level: 'Graduate',
      preferred_iitian: false,
      preferred_institutions: '',
      additional_notes: '',
    });
  };

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  const handleAdd = () => {
    const newQualification: EducationalQualification = {
      id: `temp-${Date.now()}`, // Temporary ID for frontend tracking
      ...formData,
    };

    onChange([...qualifications, newQualification]);
    resetForm();
    setIsAdding(false);
  };

  const handleEdit = (index: number) => {
    const qualification = qualifications[index];
    setFormData({
      degree_level: qualification.degree_level,
      preferred_iitian: qualification.preferred_iitian,
      preferred_institutions: qualification.preferred_institutions || '',
      additional_notes: qualification.additional_notes || '',
    });
    setEditingIndex(index);
    setIsAdding(false);
  };

  const handleUpdate = () => {
    if (editingIndex === null) return;

    const updatedQualifications = [...qualifications];
    updatedQualifications[editingIndex] = {
      ...updatedQualifications[editingIndex],
      ...formData,
    };

    onChange(updatedQualifications);
    resetForm();
    setEditingIndex(null);
  };

  const handleDelete = (index: number) => {
    const updatedQualifications = qualifications.filter((_, i) => i !== index);
    onChange(updatedQualifications);
  };

  const handleCancel = () => {
    resetForm();
    setIsAdding(false);
    setEditingIndex(null);
  };

  const handleStartAdding = () => {
    resetForm();
    setIsAdding(true);
    setEditingIndex(null);
  };

  // ============================================================================
  // Render Form
  // ============================================================================

  const renderForm = () => (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Degree Level Dropdown */}
        <div>
          <label htmlFor="degree_level" className="block text-sm font-medium text-gray-700 mb-1">
            Degree Level *
          </label>
          <select
            id="degree_level"
            value={formData.degree_level}
            onChange={(e) => handleInputChange('degree_level', e.target.value as typeof DEGREE_LEVELS[number])}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          >
            {DEGREE_LEVELS.map((level) => (
              <option key={level} value={level}>
                {level}
              </option>
            ))}
          </select>
        </div>

        {/* IIT Preference Checkbox */}
        <div className="flex items-center pt-6">
          <input
            type="checkbox"
            id="preferred_iitian"
            checked={formData.preferred_iitian}
            onChange={(e) => handleInputChange('preferred_iitian', e.target.checked)}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <label htmlFor="preferred_iitian" className="ml-2 block text-sm text-gray-700">
            Preferred IITian
          </label>
        </div>
      </div>

      {/* Preferred Institutions */}
      <div>
        <label htmlFor="preferred_institutions" className="block text-sm font-medium text-gray-700 mb-1">
          Preferred Institutions
        </label>
        <input
          type="text"
          id="preferred_institutions"
          value={formData.preferred_institutions}
          onChange={(e) => handleInputChange('preferred_institutions', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="e.g., IIT Delhi, IIT Bombay, NIT Trichy"
        />
        <p className="mt-1 text-xs text-gray-500">
          Comma-separated list of preferred institutions
        </p>
      </div>

      {/* Additional Notes */}
      <div>
        <label htmlFor="additional_notes" className="block text-sm font-medium text-gray-700 mb-1">
          Additional Notes
        </label>
        <textarea
          id="additional_notes"
          rows={2}
          value={formData.additional_notes}
          onChange={(e) => handleInputChange('additional_notes', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
          placeholder="Any additional educational requirements or preferences..."
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-2">
        <button
          type="button"
          onClick={handleCancel}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={editingIndex !== null ? handleUpdate : handleAdd}
          className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          {editingIndex !== null ? 'Update' : 'Add'} Qualification
        </button>
      </div>
    </div>
  );

  // ============================================================================
  // Render Qualification Card
  // ============================================================================

  const renderQualificationCard = (qualification: EducationalQualification, index: number) => (
    <div
      key={qualification.id || index}
      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
    >
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center space-x-2 mb-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
              {qualification.degree_level}
            </span>
            {qualification.preferred_iitian && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Preferred IITian
              </span>
            )}
          </div>

          {qualification.preferred_institutions && (
            <div className="mb-2">
              <p className="text-sm font-medium text-gray-700">Preferred Institutions:</p>
              <p className="text-sm text-gray-600">{qualification.preferred_institutions}</p>
            </div>
          )}

          {qualification.additional_notes && (
            <div>
              <p className="text-sm font-medium text-gray-700">Additional Notes:</p>
              <p className="text-sm text-gray-600">{qualification.additional_notes}</p>
            </div>
          )}
        </div>

        <div className="flex space-x-2 ml-4">
          <button
            type="button"
            onClick={() => handleEdit(index)}
            className="px-2 py-1 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
            title="Edit qualification"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={() => handleDelete(index)}
            className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            title="Delete qualification"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <div className="space-y-4">
      {/* Section Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium text-gray-900">Educational Qualifications</h3>
          <p className="text-sm text-gray-500 mt-1">
            Specify the educational requirements for this position
          </p>
        </div>
        {!isAdding && editingIndex === null && (
          <button
            type="button"
            onClick={handleStartAdding}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 transition-colors"
          >
            + Add Qualification
          </button>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Add/Edit Form */}
      {(isAdding || editingIndex !== null) && renderForm()}

      {/* Qualifications List */}
      {qualifications.length > 0 ? (
        <div className="space-y-3">
          {qualifications.map((qualification, index) => 
            renderQualificationCard(qualification, index)
          )}
        </div>
      ) : (
        !isAdding && (
          <div className="text-center py-8 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg">
            <p className="text-sm text-gray-500">
              No educational qualifications added yet.
            </p>
            <button
              type="button"
              onClick={handleStartAdding}
              className="mt-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Add your first qualification
            </button>
          </div>
        )
      )}
    </div>
  );
};
